// Pages/Assets/WithdrawPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Button,
  Alert,
  Divider,
  InputAdornment,
  Tooltip,
  Avatar,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import HistoryIcon from "@mui/icons-material/History";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AssignmentReturnedIcon from "@mui/icons-material/AssignmentReturned";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { fetchSpotBalances } from "../../store/Slices/assetSlice";
import {
  createWithdrawal,
  fetchMyWithdrawals,
  feeQuote,
  selectQuote,
  selectQuoteLoading,
} from "../../store/Slices/withdrawSlice";

// أيقوناتك
import usdtIcon from "../../assets/usdt.png";
import btcIcon from "../../assets/bitcoin.png";
import ltcIcon from "../../assets/litecoin.png";
import xrpIcon from "../../assets/xrp.png";
// لو عندك user في auth:
const selectAuthUser = (s) => s.auth?.user || null;

const COIN_META = {
  USDT: {
    name: "Tether",
    icon: usdtIcon,
    networks: ["TRC20", "ERC20", "BEP20"],
  },
  BTC: { name: "Bitcoin", icon: btcIcon, networks: ["BTC"] },
  LTC: { name: "Litecoin", icon: ltcIcon, networks: ["LTC"] },
  XRP: { name: "XRP", icon: xrpIcon, networks: ["XRP"] },
};
const SUPPORTED = Object.keys(COIN_META);

const addressHintByNetwork = (n) => {
  switch (n) {
    case "TRC20":
      return "Example: T... (TRON)";
    case "ERC20":
    case "BEP20":
      return "Example: 0xabc... (EVM)";
    case "BTC":
      return "Example: bc1..., 1..., or 3...";
    case "LTC":
      return "Example: ltc1..., L..., or M...";
    case "XRP":
      return "Example: r... (Ripple)";
    default:
      return "Enter the recipient address";
  }
};

const validateAddress = (network, addr) => {
  const a = String(addr || "").trim();
  if (!a) return "Recipient address is required.";
  switch (network) {
    case "TRC20":
      return /^T[1-9A-HJ-NP-Za-km-z]{25,36}$/.test(a)
        ? ""
        : "Invalid TRC20 address.";
    case "ERC20":
    case "BEP20":
      return /^0x[a-fA-F0-9]{40}$/.test(a) ? "" : "Invalid 0x address.";
    case "BTC":
      return /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,62}$/.test(a)
        ? ""
        : "Invalid BTC address.";
    case "LTC":
      return /^(ltc1|[LM])[a-zA-HJ-NP-Z0-9]{25,62}$/.test(a)
        ? ""
        : "Invalid LTC address.";
    case "XRP":
      return /^r[1-9A-HJ-NP-Za-km-z]{24,35}$/.test(a)
        ? ""
        : "Invalid XRP address.";
    default:
      return "Select a network first.";
  }
};

const fmt = (n, d = 8) => Number(n ?? 0).toFixed(d);

export default function WithdrawPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authUser = useSelector(selectAuthUser); // يحتوي kyc_status إن كنتَ أضفته في auth
  const { spotBalances = [] } = useSelector((s) => s.assets ?? {});
  const quote = useSelector(selectQuote);
  const quoteLoading = useSelector(selectQuoteLoading);

  const accentBg = `linear-gradient(90deg, ${alpha(
    theme.palette.primary.main,
    0.12
  )}, ${alpha(theme.palette.primary.main, 0.02)})`;
  const panel = {
    borderRadius: 3,
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    boxShadow: "none",
  };

  // Form state
  const [asset, setAsset] = useState("USDT");
  const [network, setNetwork] = useState("TRC20");
  const [toAddress, setToAddress] = useState("");
  const [amount, setAmount] = useState("");

  // UX
  const [serverError, setServerError] = useState("");
  const [serverSuccess, setServerSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchSpotBalances());
    dispatch(fetchMyWithdrawals());
  }, [dispatch]);

  const available = useMemo(() => {
    const row =
      spotBalances.find((b) => (b?.asset_id?.symbol || b?.symbol) === asset) ||
      {};
    return Number(row?.balance || 0);
  }, [spotBalances, asset]);

  const amountError = useMemo(() => {
    if (!amount) return "Amount is required.";
    const n = Number(amount);
    if (!isFinite(n) || n <= 0) return "Enter a valid amount.";
    if (n > available)
      return `Amount exceeds available balance (${fmt(available)} ${asset}).`;
    return "";
  }, [amount, available, asset]);

  const addressError = useMemo(() => validateAddress(network, toAddress), [
    network,
    toAddress,
  ]);

  const canSubmitBase =
    asset &&
    network &&
    !amountError &&
    !addressError &&
    Number(amount) > 0 &&
    !submitting;

  // نحسب قيمة المبلغ بالدولار (USDT) من الاقتباس إن وُجد؛ وإلا افتراضيًا 1 لو USDT
  const amountUSDT = useMemo(() => {
    const amt = Number(amount || 0);
    const px = Number(quote?.price_usdt || (asset === "USDT" ? 1 : 0));
    return isFinite(amt) && isFinite(px) && px > 0 ? amt * px : 0;
  }, [amount, quote, asset]);

  const needsKyc =
    amountUSDT > 56 && String(authUser?.kyc_status) !== "verified";
  const canSubmit = canSubmitBase && !needsKyc;

  const onQuote = () => {
    const n = Number(amount);
    if (!isFinite(n) || n <= 0) return;
    dispatch(feeQuote({ asset_symbol: asset, amount: n }));
  };

  useEffect(() => {
    onQuote();
  }, [asset, amount]);

  const onMax = () => setAmount(String(available));

  const pasteFromClipboard = async () => {
    try {
      const t = await navigator.clipboard.readText();
      if (t) setToAddress(t.trim());
    } catch {}
  };
  const copy = async (v) => {
    try {
      await navigator.clipboard.writeText(v || "");
    } catch {}
  };

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setServerError("");
    setServerSuccess("");
    try {
      await dispatch(
        createWithdrawal({
          asset_symbol: asset,
          network,
          to_address: toAddress.trim(),
          amount: Number(amount),
        })
      ).unwrap();

      setServerSuccess(
        "Your request has been submitted. You’ll receive an email upon completion."
      );
      setAmount("");
      setToAddress("");
      dispatch(fetchSpotBalances());
      dispatch(fetchMyWithdrawals());
    } catch (errMsg) {
      // التقاط رسالة KYC_REQUIRED من السيرفر وتحويلها إلى CTA
      if (/KYC/i.test(String(errMsg))) {
        setServerError("");
      } else {
        setServerError(errMsg || "Failed to submit the withdrawal.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100dvh" }}>
      {/* TopNav */}
      <AppBar
        position="sticky"
        elevation={0}
        color="transparent"
        sx={{
          bgcolor: "transparent",
          color: "text.primary",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIosNewIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1 }} color="inherit">
            Withdraw
          </Typography>
          <Tooltip title="History">
            <IconButton
              color="inherit"
              onClick={() => navigate("/withdraw/history")}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Form */}
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", md: "1fr 0.9fr" },
          backgroundImage: accentBg,
          backgroundSize: "100% 180px",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Card sx={panel}>
          <CardContent>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Avatar
                src={COIN_META[asset]?.icon}
                sx={{ width: 28, height: 28 }}
              />
              <Typography variant="h6">Withdraw {asset}</Typography>
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label="Spot Wallet"
              />
            </Stack>

            <Alert severity="info" sx={{ mb: 2 }}>
              <b>Fees:</b> Platform fee is <b>5 USDT</b> (converted to the
              withdrawn asset at current price). For non-USDT assets an
              additional <b>1% network fee</b> applies. Net = Amount − (Platform
              fee + Network fee).
            </Alert>

            {/* إشعار KYC في الواجهة */}
            {needsKyc && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please verify your account first USDT.&nbsp;
                <Button
                  size="small"
                  variant="text"
                  onClick={() => navigate("/verify")}
                >
                  Go to verification
                </Button>
              </Alert>
            )}

            {!!serverError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {serverError}
              </Alert>
            )}
            {!!serverSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {serverSuccess}
              </Alert>
            )}

            <TextField
              select
              label="Asset"
              fullWidth
              value={asset}
              onChange={(e) => {
                const sym = e.target.value;
                setAsset(sym);
                setNetwork(COIN_META[sym]?.networks?.[0] || "");
              }}
              sx={{ mb: 2 }}
            >
              {SUPPORTED.map((sym) => (
                <MenuItem key={sym} value={sym}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar
                      src={COIN_META[sym].icon}
                      sx={{ width: 20, height: 20 }}
                    />
                    <span>
                      {sym} — {COIN_META[sym].name}
                    </span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Network"
              fullWidth
              value={network}
              onChange={(e) => setNetwork(e.target.value)}
              sx={{ mb: 2 }}
            >
              {(COIN_META[asset]?.networks || []).map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Recipient Address"
              fullWidth
              value={toAddress}
              onChange={(e) => setToAddress(e.target.value)}
              error={!!addressError}
              helperText={addressError || addressHintByNetwork(network)}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Paste">
                      <IconButton size="small" onClick={pasteFromClipboard}>
                        <AssignmentReturnedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => copy(toAddress)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Amount"
              fullWidth
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(",", "."))}
              error={!!amountError}
              helperText={
                amountError || "Enter the amount you wish to withdraw."
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button size="small" onClick={onMax}>
                      MAX
                    </Button>
                  </InputAdornment>
                ),
                inputMode: "decimal",
              }}
            />

            <Box sx={{ mt: 1.5, color: "text.secondary" }}>
              Available (Spot):{" "}
              <b>
                {fmt(available)} {asset}
              </b>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Quote summary */}
            <Box sx={{ display: "grid", gap: 1.2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary" }}
                >
                  Fee Summary
                </Typography>
                {quoteLoading ? <CircularProgress size={16} /> : null}
                <Button
                  size="small"
                  onClick={onQuote}
                  startIcon={<RefreshIcon fontSize="small" />}
                >
                  Refresh
                </Button>
              </Stack>

              {quote ? (
                <>
                  <Row label="Price (USDT)">{fmt(quote.price_usdt, 4)}</Row>
                  <Row label="Platform fee (5 USDT)">
                    {asset === "USDT"
                      ? `5.00000000 ${asset}`
                      : `${fmt(quote.platform_fee_asset)} ${asset}  (≈ 5 USDT)`}
                  </Row>
                  <Row label="Network fee">
                    {asset === "USDT"
                      ? `0.00000000 ${asset}`
                      : `${fmt(quote.network_fee_asset)} ${asset}  (1%)`}
                  </Row>
                  <Row label="Total fees">
                    {fmt(quote.total_fee_asset)} {asset}
                  </Row>
                  <Row label="Estimated net">
                    {fmt(quote.net_amount)} {asset}
                  </Row>
                </>
              ) : (
                <Alert severity="info">
                  Enter amount to preview fees and net.
                </Alert>
              )}
            </Box>

            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={!canSubmit}
              sx={{ mt: 2 }}
            >
              Confirm Withdrawal
            </Button>
          </CardContent>
        </Card>

        {/* تذكيرات */}
        <Card sx={panel}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
              Reminders
            </Typography>
            <ul style={{ margin: 0, paddingInlineStart: 18, lineHeight: 1.6 }}>
              <li>
                Ensure the selected network matches the recipient address
                format.
              </li>
              <li>
                <b>Platform fee</b> is 5 USDT, converted to the withdrawn asset
                at current price.
              </li>
              <li>
                <b>Network fee</b> is 1% for all non-USDT assets.
              </li>
              <li>
                Withdrawals are executed from your <b>Spot Wallet</b>.
              </li>
            </ul>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

function Row({ label, children }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Stack>
  );
}
